import React, { useCallback } from 'react';
import type { ColDef, GridReadyEvent } from 'ag-grid-community';

import AgGrid from '../../shared/agGrid/agGrid.component';
import { gridPivotOptions } from '../../shared/customReports/constants';
import { uniqListAggregation } from './aggregators';
import { useSortOnColumnRowGroupChanged } from './useSortOnColumnRowGroupChanged';
import useGridApi from '../../shared/hooks/useGridApi.hook';
import { useRestoreColumnsState } from '../shared/useRestoreColumnsState.hook';
import { useCurrentClientId } from '../shared/utils';
import Button from '../../shared/button.component';

const DEFAULT_COLUMN_GROUP_ROWS = ['COMPANY_CODE'];

const ScenarioMemberListGrid = ({
  isLoading,
  calcData,
  scenarioId,
  compareScenarioId,
  onGridReady: onGridReadyProp,
  columnDefinitions,
}: {
  isLoading: boolean,
  calcData: unknown[],
  scenarioId: number,
  compareScenarioId?: number | null,
  onGridReady: (event: GridReadyEvent) => void,
  columnDefinitions: ColDef[],
}) => {
  const clientId = useCurrentClientId();
  const { onGridReady, gridApi } = useGridApi({ onGridReady: onGridReadyProp });

  const {
    resetColumnState,
    onColumnRowGroupChanged: onColumnRowGroupChangedSave,
    ...restoreColumnStateGridProps
  } = useRestoreColumnsState(`scenario-members-grid-${scenarioId}@client-${clientId}`, gridApi, {
    defaultColumnGroups: DEFAULT_COLUMN_GROUP_ROWS,
  });

  const {
    onColumnRowGroupChanged: onColumnRowGroupChangedSort,
  } = useSortOnColumnRowGroupChanged(DEFAULT_COLUMN_GROUP_ROWS);

  const onColumnRowGroupChanged = useCallback(event => {
    onColumnRowGroupChangedSave(event);
    onColumnRowGroupChangedSort(event);
  }, [onColumnRowGroupChangedSave, onColumnRowGroupChangedSort]);

  return (
    <div className="ag-grid-full-page-with-secondary-menu">
      <div className="d-flex justify-content-end mb-2">
        <Button kind="secondary" onClick={resetColumnState}>
          Reset grid view
        </Button>
      </div>
      <AgGrid
        columnDefs={columnDefinitions}
        rowData={calcData}
        isGridLoading={isLoading}
        onGridReady={onGridReady}
        {...gridPivotOptions}
        aggFuncs={{
          uniqList: uniqListAggregation,
        }}
        groupRemoveLowestSingleChildren
        groupTotalRow={false}
        context={{
          compareScenarioId,
          scenarioId,
        }}
        onColumnRowGroupChanged={onColumnRowGroupChanged}
        {...restoreColumnStateGridProps}
      />
    </div>
  );
};

export default ScenarioMemberListGrid;
